import { useEffect, useMemo, useState } from 'react'
import { useI18n } from 'builder/hooks/useI18n'

import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { Skeleton } from 'builder/Skeleton/Skeleton'
import { GradingFeedback, Interview, InterviewAnswer } from 'builder/modules/interview/types'
import { VideoPlayer } from '../VideoPlayer/VideoPlayer'
import { Badge, BadgeEnum } from '../Badge/Badge'

import { ShowStyledHTML } from '../ShowStyledHTML/ShowStyledHTML'
import { ShortOrOffTrackSection } from '../ShortOrOffTrackSection/ShortOrOffTrackSection'
import {
  BadgeContainer,
  ChevronDown,
  ChevronUp,
  Section,
  SectionContainer,
  SectionTextContainer,
  ShowMoreLess,
  VideoContainer,
  VideoSection,
} from './styles'

export const TRANSLATION_PREFIX = `builder.interview_prep_view.feedbackV2.feedback_section`

interface Props {
  answer: InterviewAnswer | undefined
  interview: Interview | undefined
  selectedQuestionIndex: number
  interviewId: number
}

export const FeedbackSection = ({
  answer,
  interview,
  selectedQuestionIndex,
  interviewId,
}: Props) => {
  const { i18n } = useI18n()
  const { isPhone } = useMediaQueries()
  const gradingFeedback = answer?.question_related_feedback?.grading_feedback
  const aiOutput = answer?.feedback?.ai_output
  const feedbackSection = useMemo(() => {
    const strengthsFromAiOutput = (aiOutput || []).filter(item => item.section === 'strengths')
    if (gradingFeedback && strengthsFromAiOutput.length > 0) {
      return [...gradingFeedback, ...strengthsFromAiOutput]
    }

    return gradingFeedback
  }, [gradingFeedback, aiOutput])

  const [seeAllAreaImprovement, setSeeAllAreaImprovement] = useState<boolean | null>(null)
  const isShortOrOffTrackSection = answer && answer?.duration < 10

  const areasOfImprovement = useMemo(() => {
    if (feedbackSection) {
      return feedbackSection.filter(
        (item: GradingFeedback) => item.section === 'areas_for_improvement',
      )
    }
    const result: GradingFeedback[] = []

    if (
      answer?.feedback?.ton_of_voice_summary_headline_areas_for_improvement &&
      answer?.feedback?.ai_summary_areas_for_improvement
    ) {
      result.push({
        code: '0',
        score: 0,
        section: 'areas_for_improvement',
        feedback: answer?.feedback?.ai_summary_areas_for_improvement,
        headline: '',
        subtitle: answer?.feedback?.ton_of_voice_summary_headline_areas_for_improvement,
      })
    }

    if (
      answer?.feedback?.areas_for_improvement &&
      answer?.feedback?.areas_for_improvement_headline
    ) {
      result.push({
        code: '1',
        score: 0,
        section: 'areas_for_improvement',
        feedback: answer?.feedback?.areas_for_improvement,
        headline: '',
        subtitle: answer?.feedback?.areas_for_improvement_headline,
      })
    }
    return result
  }, [answer, feedbackSection])

  useEffect(() => {
    if (areasOfImprovement.length > 2) {
      setSeeAllAreaImprovement(true)
    }
  }, [areasOfImprovement])

  const processedAreasOfImprovement = useMemo(() => {
    return areasOfImprovement.slice(0, seeAllAreaImprovement ? 2 : undefined)
  }, [areasOfImprovement, seeAllAreaImprovement])

  const areasOfStrength: GradingFeedback[] = useMemo(() => {
    if (feedbackSection) {
      return feedbackSection.filter((item: GradingFeedback) => item.section === 'strengths')
    }
    const result: GradingFeedback[] = []
    if (
      answer?.feedback?.ai_summary_strengths &&
      answer?.feedback?.ton_of_voice_summary_headline_strengths
    ) {
      result.push({
        code: '0',
        score: 0,
        section: 'strengths',
        feedback: answer?.feedback?.ai_summary_strengths,
        headline: '',
        subtitle: answer?.feedback?.ton_of_voice_summary_headline_strengths,
      })
    }
    if (answer?.feedback?.strengths && answer?.feedback?.strengths_headline) {
      result.push({
        code: '1',
        score: 0,
        section: 'strengths',
        feedback: answer?.feedback?.strengths,
        headline: '',
        subtitle: answer?.feedback?.strengths_headline,
      })
    }

    return result
  }, [answer, feedbackSection])

  function renderBadge(text: string, color: BadgeEnum, hasContent: boolean, title?: string) {
    if (
      answer?.ai_speech_job?.status === 'success' &&
      answer?.ai_job?.status === 'success' &&
      !hasContent
    ) {
      return null
    }

    if (
      answer?.ai_speech_job?.status == null ||
      answer?.ai_speech_job?.status === 'success' ||
      answer?.ai_speech_job?.status === 'pending'
    ) {
      return (
        <BadgeContainer>
          {answer?.ai_speech_job?.status == null || answer?.ai_speech_job?.status === 'pending' ? (
            <Skeleton width={100} height={20} />
          ) : (
            <Badge text={text} title={title} type={color} />
          )}
        </BadgeContainer>
      )
    }
    return null
  }

  const answerFromInterview = useMemo(() => {
    return interview?.questions[selectedQuestionIndex]?.answer
  }, [interview, selectedQuestionIndex])

  return (
    <>
      <SectionContainer>
        <VideoSection>
          {renderBadge(
            i18n.t(`${TRANSLATION_PREFIX}.strengths${isPhone ? '_mobile' : ''}`),
            BadgeEnum.green,
            !!(
              answer?.feedback?.ton_of_voice_summary_headline_strengths ||
              answer?.feedback?.strengths_headline ||
              answer?.feedback?.ai_output ||
              answer?.question_related_feedback?.strengths_headline
            ),
          )}

          <VideoContainer>
            <VideoPlayer
              answerDurationInSeconds={answer?.duration}
              audioUrl={answerFromInterview?.audio}
              videoUrl={answerFromInterview?.video}
            />
          </VideoContainer>
        </VideoSection>

        {!isShortOrOffTrackSection &&
          areasOfStrength?.length > 0 &&
          areasOfStrength.map(area => (
            <Section key={area.code} data-code={area.code}>
              {renderBadge(
                area.subtitle,
                BadgeEnum.green,
                !!(area.subtitle || area.headline),
                area.headline,
              )}
              <SectionTextContainer>
                <ShowStyledHTML dangerouslySetInnerHTML={{ __html: area.feedback }} />
              </SectionTextContainer>
            </Section>
          ))}
      </SectionContainer>

      {isShortOrOffTrackSection && <ShortOrOffTrackSection interviewId={interviewId} />}

      {!isShortOrOffTrackSection && (
        <SectionContainer>
          {renderBadge(
            i18n.t(`${TRANSLATION_PREFIX}.areas_for_improvement`),
            BadgeEnum.orange,
            !!(
              answer?.feedback?.ton_of_voice_summary_headline_areas_for_improvement ||
              answer?.feedback?.areas_for_improvement_headline ||
              answer?.question_related_feedback?.areas_for_improvement_headline
            ),
          )}

          {processedAreasOfImprovement?.length > 0 &&
            processedAreasOfImprovement.map(area => (
              <Section key={area.code} data-code={area.code}>
                {renderBadge(
                  area.subtitle,
                  BadgeEnum.orange,
                  !!(area.subtitle || area.headline),
                  area.headline,
                )}
                <SectionTextContainer>
                  <ShowStyledHTML dangerouslySetInnerHTML={{ __html: area.feedback }} />
                </SectionTextContainer>
              </Section>
            ))}

          {typeof seeAllAreaImprovement === 'boolean' && (
            <div>
              <ShowMoreLess onClick={() => setSeeAllAreaImprovement(prevState => !prevState)}>
                {i18n.t(
                  `${TRANSLATION_PREFIX}.${seeAllAreaImprovement ? 'show_more' : 'show_less'}`,
                )}

                {seeAllAreaImprovement ? <ChevronDown /> : <ChevronUp />}
              </ShowMoreLess>
            </div>
          )}
        </SectionContainer>
      )}
    </>
  )
}
